// API Configuration
const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3000/api/v1';
let authToken = localStorage.getItem('auth_token') || null;

const headers = (extra = {}) => ({
  ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}),
  ...extra,
});

const jsonHeaders = () => headers({ 'Content-Type': 'application/json' });

const handleResponse = async (response) => {
  let data;
  try {
    data = await response.json();
  } catch {
    // Response body is not valid JSON (e.g. rate-limit plain text)
    const text = `HTTP ${response.status}: Non-JSON response`;
    const error = new Error(text);
    error.status = response.status;
    error.data = { error: text };
    throw error;
  }
  if (!response.ok) {
    const error = new Error(data.error || `HTTP ${response.status}`);
    error.status = response.status;
    error.data = data;
    throw error;
  }
  return data;
};

export const api = {
  // Token management
  setToken: (token) => { authToken = token; },
  getToken: () => authToken,

  // ─── Auth ──────────────────────────────────────────
  login: async (email, password) => {
    const res = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    return handleResponse(res);
  },

  register: async (name, email, password, company) => {
    const res = await fetch(`${API_BASE}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, email, password, company }),
    });
    return handleResponse(res);
  },

  getMe: async () => {
    const res = await fetch(`${API_BASE}/auth/me`, { headers: headers() });
    return handleResponse(res);
  },

  // ─── Documents ─────────────────────────────────────
  createDocument: async (formData) => {
    const res = await fetch(`${API_BASE}/documents`, {
      method: 'POST',
      headers: headers(), // no Content-Type for FormData
      body: formData,
    });
    return handleResponse(res);
  },

  // Documents where the logged-in user is a recipient (matched by email)
  getDocumentsToSign: async () => {
    const res = await fetch(`${API_BASE}/documents/to-sign`, { headers: headers() });
    return handleResponse(res);
  },

  getDocuments: async (page = 1, limit = 10, status = '') => {
    const params = new URLSearchParams({ page, limit });
    if (status) params.append('status', status);
    const res = await fetch(`${API_BASE}/documents?${params}`, { headers: headers() });
    return handleResponse(res);
  },

  getDocument: async (documentId) => {
    const res = await fetch(`${API_BASE}/documents/${documentId}`, { headers: headers() });
    return handleResponse(res);
  },

  resendInvitation: async (documentId, recipientId) => {
    const res = await fetch(`${API_BASE}/documents/${documentId}/resend/${recipientId}`, {
      method: 'POST',
      headers: headers(),
    });
    return handleResponse(res);
  },

  cancelDocument: async (documentId) => {
    const res = await fetch(`${API_BASE}/documents/${documentId}/cancel`, {
      method: 'POST',
      headers: headers(),
    });
    return handleResponse(res);
  },

  getDocumentActivities: async (documentId) => {
    const res = await fetch(`${API_BASE}/documents/${documentId}/activities`, { headers: headers() });
    return handleResponse(res);
  },

  downloadDocument: async (documentId) => {
    const res = await fetch(`${API_BASE}/documents/${documentId}/download`, { headers: headers() });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.blob();
  },

  // ─── Signing ───────────────────────────────────────
  getSigningData: async (signingToken) => {
    const res = await fetch(`${API_BASE}/signing/${signingToken}`);
    return handleResponse(res);
  },

  saveSignature: async (signingToken, signatureData) => {
    const res = await fetch(`${API_BASE}/signing/${signingToken}/signature`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(signatureData),
    });
    return handleResponse(res);
  },

  fillField: async (signingToken, fieldId, value) => {
    const res = await fetch(`${API_BASE}/signing/${signingToken}/fields/${fieldId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ value }),
    });
    return handleResponse(res);
  },

  verifySigningPassword: async (signingToken, password) => {
    const res = await fetch(`${API_BASE}/signing/${signingToken}/verify-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password }),
    });
    return handleResponse(res);
  },

  // Verify host identity for in-person signing
  // auth_token: the JWT from localStorage (or obtained via login)
  verifyHost: async (signingToken, auth_token) => {
    const res = await fetch(`${API_BASE}/signing/${signingToken}/verify-host`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ auth_token }),
    });
    return handleResponse(res);
  },

  completeSigning: async (signingToken, { location } = {}) => {
    const res = await fetch(`${API_BASE}/signing/${signingToken}/complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ location: location || null }),
    });
    return handleResponse(res);
  },

  previewPDF: async (signingToken) => {
    const res = await fetch(`${API_BASE}/signing/${signingToken}/preview`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.blob();
  },

  // ─── Bundles (v1.4.0) ─────────────────────────────
  createBundle: async (formData) => {
    const res = await fetch(`${API_BASE}/documents/bundle`, {
      method: 'POST',
      headers: headers(), // no Content-Type for FormData
      body: formData,
    });
    return handleResponse(res);
  },

  getBundleStatus: async (bundleId) => {
    const res = await fetch(`${API_BASE}/documents/bundle/${bundleId}`, { headers: headers() });
    return handleResponse(res);
  },

  listBundles: async (page = 1, limit = 20) => {
    const params = new URLSearchParams({ page, limit });
    const res = await fetch(`${API_BASE}/documents/bundles/list?${params}`, { headers: headers() });
    return handleResponse(res);
  },

  downloadBundleFiles: async (bundleId, options = {}) => {
    const res = await fetch(`${API_BASE}/documents/bundle/${bundleId}/download`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify(options),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.blob();
  },

  // ─── Signature Field Placement ───────────────────
  addSignatureField: async (documentId, fieldData) => {
    const res = await fetch(`${API_BASE}/documents/${documentId}/signature-fields`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify(fieldData),
    });
    return handleResponse(res);
  },

  deleteSignatureField: async (documentId, fieldId) => {
    const res = await fetch(`${API_BASE}/documents/${documentId}/signature-fields/${fieldId}`, {
      method: 'DELETE',
      headers: headers(),
    });
    return handleResponse(res);
  },

  getDocumentPdfUrl: (documentId) => `${API_BASE}/documents/${documentId}/pdf`,

  downloadCertificate: async (documentId) => {
    const res = await fetch(`${API_BASE}/documents/${documentId}/certificate`, { headers: headers() });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.blob();
  },

  downloadCombined: async (documentId) => {
    const res = await fetch(`${API_BASE}/documents/${documentId}/download?combine=true`, { headers: headers() });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.blob();
  },

  // Download document files with options (combine, zip, etc.)
  downloadDocumentFiles: async (documentId, options = {}) => {
    const res = await fetch(`${API_BASE}/documents/${documentId}/download-files`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify(options),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.blob();
  },
};
